import { useRef, useState } from 'react';
import * as XLSX from 'xlsx';

export type BulkResult = { row: number; ok: boolean; message?: string };

type Column = { key: string; header: string; example: string };

type Props = {
  title: string;
  hint: string;
  columns: Column[];
  templateFileName: string;
  /** 헤더가 매핑된 행 객체 배열을 서버로 보내고 행별 결과를 돌려준다. */
  onSubmit: (rows: Record<string, string>[]) => Promise<BulkResult[] | null>;
  onClose: () => void;
};

const pad = (n: number) => String(n).padStart(2, '0');

function cellToString(v: unknown, key: string): string {
  if (v === null || v === undefined) return '';
  if (v instanceof Date) return `${v.getFullYear()}-${pad(v.getMonth() + 1)}-${pad(v.getDate())}`;
  // 엑셀이 010... 연락처를 숫자로 저장하면 앞자리 0이 사라진다.
  if (typeof v === 'number' && key.toLowerCase().includes('phone')) {
    const s = String(v);
    return s.length === 10 && s.startsWith('1') ? `0${s}` : s;
  }
  return String(v).trim();
}

export default function ExcelBulkImport({
  title,
  hint,
  columns,
  templateFileName,
  onSubmit,
  onClose,
}: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<BulkResult[] | null>(null);

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([columns.map((c) => c.header), columns.map((c) => c.example)]);
    ws['!cols'] = columns.map((c) => ({ wch: Math.max(c.header.length, c.example.length) + 6 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '등록양식');
    XLSX.writeFile(wb, templateFileName);
  };

  const onFile = async (file: File | undefined) => {
    setResults(null);
    setError('');
    setRows([]);
    if (!file) return;
    setFileName(file.name);
    try {
      const wb = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: true });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });
      const parsed = raw
        .map((r) => {
          const out: Record<string, string> = {};
          columns.forEach((c) => {
            out[c.key] = cellToString(r[c.header], c.key);
          });
          return out;
        })
        .filter((r) => Object.values(r).some((v) => v !== ''));

      if (raw.length > 0 && columns.some((c) => !(c.header in raw[0]))) {
        setError('양식의 열 이름이 다릅니다. "양식 다운로드"로 받은 파일을 사용해 주세요.');
        return;
      }
      if (parsed.length === 0) {
        setError('등록할 데이터가 없습니다. 2행부터 입력해 주세요.');
        return;
      }
      setRows(parsed);
    } catch (err) {
      console.error('excel parse', err);
      setError('엑셀 파일을 읽지 못했습니다. .xlsx/.xls/.csv 파일인지 확인해 주세요.');
    }
  };

  const submit = async () => {
    setBusy(true);
    setError('');
    const res = await onSubmit(rows);
    setBusy(false);
    if (res) setResults(res);
    else setError('일괄 등록에 실패했습니다. 잠시 후 다시 시도해 주세요.');
  };

  const okCount = results?.filter((r) => r.ok).length ?? 0;
  const failed = results?.filter((r) => !r.ok) ?? [];

  return (
    <div className="card card-pad mb16">
      <div className="card-title">
        {title}{' '}
        <button className="btn btn-ghost btn-sm" onClick={onClose}>
          닫기
        </button>
      </div>
      <div className="muted mb16">{hint}</div>

      <div className="flex mb16">
        <button className="btn btn-outline btn-sm" onClick={downloadTemplate}>
          📄 양식 다운로드
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={(e) => onFile(e.target.files?.[0])}
        />
      </div>

      {error && <div className="badge badge-danger mb16">{error}</div>}

      {rows.length > 0 && !results && (
        <>
          <div className="muted mb16">
            {fileName} · {rows.length}행 (미리보기 상위 5행)
          </div>
          <div className="tbl-wrap mb16">
            <table className="tbl">
              <thead>
                <tr>
                  {columns.map((c) => (
                    <th key={c.key}>{c.header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 5).map((r, i) => (
                  <tr key={i}>
                    {columns.map((c) => (
                      <td key={c.key}>{c.key === 'password' ? '••••••' : r[c.key]}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button className="btn btn-primary" onClick={submit} disabled={busy}>
            {busy ? '등록 중...' : `${rows.length}건 일괄 등록`}
          </button>
        </>
      )}

      {results && (
        <>
          <div className="mb16">
            ✅ 성공 {okCount}건 · ❌ 실패 {failed.length}건
          </div>
          {failed.length > 0 && (
            <div className="row-list mb16">
              {failed.map((f) => (
                <div className="row" key={f.row}>
                  <div className="grow">
                    <div className="t">
                      {f.row + 1}행 {rows[f.row - 1]?.name}
                    </div>
                    <div className="s">{f.message}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
